import { ArrowLeft, Check, FilePlus, Pencil, RotateCcw, X } from 'lucide-react';
import type { Lead } from '../../../../types';
import { contactDisplayName } from '../../../../types';
import { LeadCategoryPicker } from '../../../../components/LeadCategoryPicker';
import { ModuleLink } from '../../../../components/ModuleLink';
import { contactDetailPath, organizationDetailPath } from '../../../../utils/contactRoutes';
import { leadHeaderEurFmt } from './leadActivityHelpers';
import { LEAD_DETAIL_BTN_OUTLINE, LEAD_DETAIL_BTN_PRIMARY } from './leadDetailStyles';
import type { UseLeadDetailLeadResult } from './useLeadDetailLead';
import { projectValueContextText, projectValueLabel, projectValueTooltip } from '../../utils/projectValue';

/**
 * Desktop-Header der Projekt-Detailansicht: volle Leiste unter der AppTopBar
 * (Titel/Kontakt links, Projektwert + Aktionen rechts, Phasen darunter).
 */
export function LeadDetailDesktopHeader({
  lead,
  goBack,
  leadApi,
  onCreateDocument,
}: {
  lead: Lead;
  goBack: () => void;
  leadApi: UseLeadDetailLeadResult;
  onCreateDocument: () => void;
}) {
  const {
    leadCategories,
    handleLeadCategoryChange,
    leadTitleEditing,
    leadTitleDraft,
    setLeadTitleDraft,
    leadTitleInputDesktopRef,
    saveLeadTitleFromEdit,
    cancelLeadTitleEdit,
    startEditingLeadTitle,
    isLeadWon,
    isLeadLost,
    isSaving,
    offerNetSum,
    projectValue,
    handleReopenLead,
    handleGewonnen,
    openVerlorenModal,
    leadPipelineStages,
    handleStageClick,
  } = leadApi;

  const currentStageIdx = leadPipelineStages.findIndex(
    (s) => lead.stage_id != null && s.id === String(lead.stage_id),
  );

  const headerContactName = contactDisplayName(lead.contact).trim();
  const headerContactId = lead.contact?.id ?? lead.contact_id;
  const headerCompany =
    (lead.organization?.name ?? '').trim() || (lead.contact?.company ?? '').trim();
  const headerOrgId = lead.organization?.id;
  const headerContactTitle = [headerContactName, headerCompany].filter(Boolean).join(' · ') || undefined;
  const createdAtLabel = (() => {
    const raw = (lead.created_at ?? '').trim();
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return `Erstellt am ${d.toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    })}`;
  })();

  const subtitleMeta = (
    <>
      {lead.display_id ? <span>{lead.display_id}</span> : null}
      {lead.display_id && createdAtLabel ? (
        <span className="mx-1.5 text-slate-300" aria-hidden>
          ·
        </span>
      ) : null}
      {createdAtLabel ? <span>{createdAtLabel}</span> : null}
    </>
  );

  return (
    <div className="hidden md:block">
      <div className="flex px-3 py-3 sm:px-4 lg:px-6 items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4 min-w-0 flex-1">
          <button
            onClick={goBack}
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-app-text-muted hover:bg-app-hover transition-colors shrink-0"
            aria-label="Zurück"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              {leadTitleEditing ? (
                <input
                  ref={leadTitleInputDesktopRef}
                  type="text"
                  value={leadTitleDraft}
                  onChange={(e) => setLeadTitleDraft(e.target.value)}
                  onBlur={() => void saveLeadTitleFromEdit()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void saveLeadTitleFromEdit();
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelLeadTitleEdit();
                    }
                  }}
                  maxLength={500}
                  disabled={isSaving}
                  className="min-w-0 max-w-xl flex-1 text-lg font-semibold text-app-text bg-app-surface border border-pipedrive-blue rounded-md px-2 py-1 shadow-sm focus:outline-none focus:ring-2 focus:ring-pipedrive-blue/30"
                  aria-label="Projekt-Titel bearbeiten"
                />
              ) : (
                <button
                  type="button"
                  onClick={startEditingLeadTitle}
                  className="group flex items-center gap-2 min-w-0 max-w-full overflow-hidden text-left rounded-md px-1 -mx-1 py-0.5 hover:bg-app-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30"
                  title="Titel bearbeiten"
                >
                  <h1 className="truncate min-w-0 text-lg font-semibold text-app-text leading-snug">
                    {lead.lead_title || '—'}
                  </h1>
                  <Pencil
                    className="w-3.5 h-3.5 shrink-0 text-slate-400 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity"
                    aria-hidden
                  />
                </button>
              )}
              <LeadCategoryPicker
                value={lead.category_key ?? ''}
                categories={leadCategories}
                onChange={(key) => void handleLeadCategoryChange(key)}
                disabled={isSaving || leadCategories.length === 0}
                variant="dot"
                className="shrink-0"
              />
              {isLeadWon && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-200">
                  Gewonnen
                </span>
              )}
              {isLeadLost && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">
                  Verloren
                </span>
              )}
            </div>
            <p
              className="mt-0.5 text-[13px] text-app-text-subtle truncate"
              title={[headerContactTitle, lead.display_id, createdAtLabel].filter(Boolean).join(' · ') || undefined}
            >
              {headerContactName || headerCompany ? (
                <>
                  {headerContactName ? (
                    headerContactId != null && headerContactId !== '' ? (
                      <ModuleLink
                        to={contactDetailPath(headerContactId)}
                        className="hover:text-pipedrive-blue hover:underline"
                      >
                        {headerContactName}
                      </ModuleLink>
                    ) : (
                      <span>{headerContactName}</span>
                    )
                  ) : null}
                  {headerContactName && headerCompany ? (
                    <span className="text-slate-400">{' ('}</span>
                  ) : null}
                  {headerCompany ? (
                    headerOrgId ? (
                      <ModuleLink
                        to={organizationDetailPath(headerOrgId)}
                        className="hover:text-pipedrive-blue hover:underline"
                      >
                        {headerCompany}
                      </ModuleLink>
                    ) : (
                      <span>{headerCompany}</span>
                    )
                  ) : null}
                  {headerContactName && headerCompany ? (
                    <span className="text-slate-400">{')'}</span>
                  ) : null}
                  {(lead.display_id || createdAtLabel) ? (
                    <>
                      <span className="mx-1.5 text-slate-300" aria-hidden>
                        ·
                      </span>
                      {subtitleMeta}
                    </>
                  ) : null}
                </>
              ) : lead.display_id || createdAtLabel ? (
                subtitleMeta
              ) : (
                <span>—</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <div className="text-right shrink-0 min-w-[5.5rem]" title={projectValueTooltip(projectValue)}>
            <p className="text-[11px] font-normal text-slate-400 leading-none">{projectValue ? `${projectValueLabel(projectValue)} · netto` : projectValueLabel(projectValue)}</p>
            <p className="mt-1 text-base font-semibold text-app-text tabular-nums leading-none">
              {offerNetSum !== null ? leadHeaderEurFmt.format(offerNetSum) : '—'}
            </p>
            {projectValueContextText(projectValue) ? (
              <p className="mt-1 text-[11px] text-slate-400 tabular-nums leading-none">
                {projectValueContextText(projectValue)}
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {isLeadWon || isLeadLost ? (
              <button
                type="button"
                onClick={() => void handleReopenLead()}
                disabled={isSaving}
                className={LEAD_DETAIL_BTN_OUTLINE}
              >
                <RotateCcw className="w-4 h-4" aria-hidden />
                Wieder öffnen
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void handleGewonnen()}
                  disabled={isSaving}
                  className="inline-flex h-9 items-center justify-center gap-1.5 shrink-0 rounded-md border border-emerald-200 bg-app-surface px-3.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 transition-colors"
                >
                  <Check className="w-4 h-4" aria-hidden />
                  Gewonnen
                </button>
                <button
                  type="button"
                  onClick={openVerlorenModal}
                  disabled={isSaving}
                  className="app-btn-danger-outline inline-flex h-9 items-center justify-center gap-1.5 shrink-0 rounded-md border border-red-200 bg-app-surface px-3.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors"
                >
                  <X className="w-4 h-4" aria-hidden />
                  Verloren
                </button>
              </>
            )}
            <button type="button" onClick={onCreateDocument} className={LEAD_DETAIL_BTN_PRIMARY}>
              <FilePlus className="w-4 h-4" aria-hidden />
              Dokument erstellen
            </button>
          </div>
        </div>
      </div>

      {leadPipelineStages.length > 0 && (
        <div className="px-3 sm:px-4 lg:px-6 pb-2 pt-0 overflow-x-auto">
          <div
            className="flex w-full overflow-hidden rounded-md"
            style={{ minHeight: '28px', minWidth: 'max-content' }}
          >
            {leadPipelineStages.map((stage, index) => {
              const isDone = isLeadWon
                ? true
                : isLeadLost
                  ? false
                  : currentStageIdx >= 0
                    ? currentStageIdx >= index
                    : false;
              const isLast = index === leadPipelineStages.length - 1;
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => void handleStageClick(stage.id)}
                  disabled={isSaving}
                  className={`flex-1 min-w-0 shrink-0 flex items-center justify-center px-1.5 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed ${
                    isDone
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-slate-200 text-app-text-subtle hover:bg-slate-300'
                  }`}
                  style={{
                    clipPath: isLast
                      ? 'none'
                      : 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
                    marginLeft: index === 0 ? 0 : '-8px',
                    zIndex: leadPipelineStages.length - index,
                  }}
                >
                  <span className="truncate">{stage.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
